import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Outlet } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
  LinearProgress,
  Button,
  Chip,
  Alert
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { getProject } from '../../utils/api';
import { getProjectOverview, getCompletionMessage } from '../../utils/projectOverviewService';
import { useAuth } from '../../contexts/AuthContext';

// Drawer width
const drawerWidth = 300;

// Define the Project Overview sections
const overviewSections = [
  {
    id: 'sow-insights',
    name: 'Project Insights',
    description: 'Upload and analyze project documents',
    path: 'sow-insights',
    priority: 1
  },
  {
    id: 'project-scope',
    name: 'Project Scope',
    description: 'Define project objectives and boundaries',
    path: 'scope',
    priority: 2
  },
  {
    id: 'requirements',
    name: 'Project Requirements',
    description: 'Document key business requirements for the project',
    path: 'requirements',
    priority: 3
  },
  {
    id: 'stakeholders',
    name: 'Project Team',
    description: 'Identify and analyze key team members',
    path: 'stakeholders',
    priority: 4
  },
  {
    id: 'timeline',
    name: 'Project Timeline',
    description: 'Define high-level project timeline and milestones',
    path: 'timeline',
    priority: 5
  },
  {
    id: 'intelligence',
    name: 'Project Intelligence',
    description: 'Assistant powered by project documents & data',
    path: 'intelligence',
    priority: 6
  }
];

// Status icon component
const StatusIcon = ({ status }) => {
  switch (status) {
    case 'completed':
      return <CheckCircleIcon color="success" />;
    case 'in_progress':
      return <WarningIcon color="warning" />;
    case 'not_started':
      return <RadioButtonUncheckedIcon color="error" />;
    default:
      return <RadioButtonUncheckedIcon color="disabled" />;
  }
};

const ProjectOverviewLayout = () => {
  const { projectId, sectionId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [project, setProject] = useState(null);
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);

  // Verify project ID matches selected project from localStorage
  useEffect(() => {
    const selectedProject = localStorage.getItem('selectedProject');
    if (selectedProject) {
      try {
        const project = JSON.parse(selectedProject);
        const selectedProjectId = project.id ? project.id.toString() : null;

        // If URL projectId doesn't match selected project, redirect to correct project
        if (selectedProjectId && projectId !== selectedProjectId) {
          console.warn(`Project mismatch detected. URL has project ${projectId} but selected project is ${selectedProjectId}. Redirecting...`);
          const currentPath = window.location.pathname.split('/').slice(3).join('/'); // Get path after /projects/
          navigate(`/projects/${selectedProjectId}/${currentPath}`, { replace: true });
        }
      } catch (e) {
        console.error('Error parsing selected project:', e);
      }
    }
  }, [projectId, navigate]);

  // Find the selected section based on the URL parameter
  useEffect(() => {
    if (sectionId) {
      const section = overviewSections.find(s => s.path === sectionId);
      setSelectedSection(section || null);
    } else {
      setSelectedSection(null);
    }
  }, [sectionId]);

  // Fetch project details once
  useEffect(() => {
    const fetchProject = async () => {
      try {
        if (projectId && projectId !== ':projectId') {
          setLoading(true);
          const projectResponse = await getProject(projectId);
          if (projectResponse.success) {
            setProject(projectResponse.project);
          }
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [projectId]);

  const refreshProject = useCallback(async () => {
    try {
      if (projectId && projectId !== ':projectId') {
        const projectResponse = await getProject(projectId);
        if (projectResponse.success) {
          setProject(projectResponse.project);
        }
      }
    } catch (error) {
      console.error('Error refreshing project:', error);
    }
  }, [projectId]);

  // Refresh overview data whenever the user navigates between sections
  // so the sidebar status and progress bar stay current after saving data
  useEffect(() => {
    const fetchOverview = async () => {
      try {
        if (projectId && projectId !== ':projectId') {
          const overviewResponse = await getProjectOverview(projectId);
          if (overviewResponse.success) {
            setOverviewData(overviewResponse.overview);
          }
        }
      } catch (error) {
        console.error('Error fetching overview:', error);
      }
    };
    fetchOverview();
  }, [projectId, sectionId]);

  // Toggle drawer
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Navigate to a section
  const handleSectionClick = (section) => {
    navigate(`/projects/${projectId}/overview/${section.path}`);
    setMobileOpen(false);
  };

  // Return to project detail page
  const handleBackToProject = () => {
    navigate(`/projects/${projectId}`);
  };

  // Navigate to SAP phases
  const handleGoToSAPPhases = () => {
    navigate(`/projects/${projectId}/prepare`);
  };

  // Get section status
  const getSectionStatus = (sectionId) => {
    if (sectionId === 'intelligence') return 'completed';
    if (!overviewData || !overviewData.section_statuses) return 'not_started';

    // Map section IDs to status keys
    const statusMap = {
      'sow-insights': 'sow_insights',
      'project-scope': 'project_scope',
      'requirements': 'requirements',
      'stakeholders': 'stakeholders',
      'timeline': 'timeline'
    };

    return overviewData.section_statuses[statusMap[sectionId]] || 'not_started';
  };

  // Render the drawer content
  const drawer = (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <DescriptionIcon sx={{ mr: 1, color: '#F5821F' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#F5821F' }}>
            Project Overview
          </Typography>
        </Box>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      <Divider />

      {/* Progress Section */}
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" gutterBottom>
          Foundation Setup Progress
        </Typography>
        <LinearProgress
          variant="determinate"
          value={overviewData?.completion_percentage || 0}
          sx={{ mb: 1, height: 8, borderRadius: 1 }}
        />
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#F5821F' }}>
          {overviewData?.completion_percentage || 0}% Complete
        </Typography>
      </Box>
      <Divider />

      {/* Sections List */}
      <List>
        {overviewSections.map((section) => {
          const status = getSectionStatus(section.id);
          const isSelected = selectedSection?.id === section.id;

          return (
            <ListItem
              button
              key={section.id}
              selected={isSelected}
              onClick={() => handleSectionClick(section)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'rgba(245, 130, 31, 0.12)',
                  borderRight: 3,
                  borderColor: '#F5821F'
                },
                '&.Mui-selected:hover': {
                  backgroundColor: 'rgba(245, 130, 31, 0.20)',
                },
                '&:hover': {
                  backgroundColor: 'rgba(245, 130, 31, 0.08)',
                },
                mb: 1,
                mx: 1,
                borderRadius: 1
              }}
            >
              <ListItemIcon>
                <StatusIcon status={status} />
              </ListItemIcon>
              <ListItemText
                primary={section.name}
                secondary={section.description}
                primaryTypographyProps={{
                  fontWeight: isSelected ? 600 : 400,
                  color: isSelected ? '#F5821F' : 'inherit'
                }}
                secondaryTypographyProps={{
                  fontSize: '0.75rem'
                }}
              />
            </ListItem>
          );
        })}
      </List>
      <Divider />

      {/* Action Buttons */}
      <Box sx={{ p: 2 }}>
        {overviewData?.is_complete ? (
          <Button
            variant="contained"
            color="success"
            fullWidth
            startIcon={<CheckCircleIcon />}
            endIcon={<ArrowForwardIcon />}
            onClick={handleGoToSAPPhases}
            sx={{ mb: 1 }}
          >
            Start SAP Phases
          </Button>
        ) : (
          <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
            Complete all sections to proceed to SAP methodology phases
          </Alert>
        )}
      </Box>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Project not found
  if (!project) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" color="error">
          Project not found
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{
          width: { md: drawerWidth },
          flexShrink: { md: 0 },
          zIndex: (theme) => theme.zIndex.appBar - 1
        }}
        aria-label="project overview sections"
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              mt: '100px'
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              mt: '100px',
              height: 'calc(100vh - 100px)',
              position: 'fixed'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Mobile menu button */}
      {isMobile && !mobileOpen && (
        <IconButton
          color="primary"
          aria-label="open drawer"
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            left: 16,
            top: 80,
            zIndex: (theme) => theme.zIndex.drawer + 1,
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': {
              bgcolor: 'background.paper',
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: 0 },
          mt: 2
        }}
      >
        <Container maxWidth="xl">
          <Outlet context={{
            project,
            overviewData,
            refreshProject,
            refreshOverview: () => {
              getProjectOverview(projectId).then(res => {
                if (res.success) setOverviewData(res.overview);
              });
            }
          }} />
        </Container>
      </Box>
    </Box>
  );
};

export default ProjectOverviewLayout;
